import d3 from 'd3';
import tooltip from '../components/tooltip';
import { showCaption } from '../components/caption';

const maxAngleToShowLabelCurrent = 7;
const maxAngleToShowLabelPrev = 13;

function drawChart(selector, currentParties, previousParties, seats) {
  const elem = d3.select(document.getElementById(selector));

  const container = {
    elem,
    width: +parseInt(elem.style('width'), 10).toFixed(0),
    height: +parseInt(elem.style('height'), 10).toFixed(0),
  };

  if (container.width === 399) {
    container.width = 400;
  }

  const options = {
    showMayoria: true,
    animationDuration: 250,
    animationDelay: 50,
  };

  const margin = {
    top: 10, right: 10, bottom: 20, left: 10,
  };
  const width = container.width - margin.left - margin.right;
  const height = container.height - margin.top - margin.bottom;
  const radius = Math.min(height, width / 2);

  const pie = d3.layout.pie()
    .startAngle(-Math.PI / 2)
    .endAngle(Math.PI / 2)
    .padAngle((Math.PI / 180.0) * 0.2)
    .value((d) => d.seats)
    .sort(null);

  const scaleFactor = radius / 210;

  const arcPrevious = d3.svg.arc()
    .innerRadius(70 * scaleFactor)
    .outerRadius(124 * scaleFactor);

  const arcCurrent = d3.svg.arc()
    .innerRadius(145 * scaleFactor)
    .outerRadius(199 * scaleFactor);

  elem.select('svg').remove();

  const svg = elem.append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .attr('viewBox', [0, 0, container.width - 20, container.height - 50].join(' '))
    .append('g')
    .attr('transform', `translate(${width / 2},${height - 20})`);

  svg.append('g').attr('class', 'c-hemicycle__hemi');
  svg.append('g').attr('class', 'c-hemicycle__labels c-hemicycle__labels--current');
  svg.append('g').attr('class', 'c-hemicycle__labels c-hemicycle__labels--previous');

  if (currentParties.length === 0) {
    currentParties.push({
      partyName: window.ac_labels.hemicycle.unassigned,
      partyAgg: window.ac_labels.hemicycle.unassigned,
      partyCode: window.ac_labels.hemicycle.unassigned.toLowerCase().replace(/\s/g, ''),
      seats,
      votesPct: 0,
    });
  } else {
    const seatsAssigned = currentParties.reduce((accumulator, party) => accumulator + party.seats, 0);
    if (seats - seatsAssigned > 0) {
      currentParties.push({
        partyName: window.ac_labels.hemicycle.unassigned,
        partyAgg: window.ac_labels.hemicycle.unassigned,
        partyCode: window.ac_labels.hemicycle.unassigned.toLowerCase().replace(/\s/g, ''),
        seats: seats - seatsAssigned,
        votesPct: 0,
      });
    }
  }

  const pathCurrent = svg.select('.c-hemicycle__hemi').datum(currentParties).selectAll('path.current')
    .data(pie, (d) => d.data.partyId);

  pathCurrent.enter().append('path')
    .attr('class', (d) => {
      showCaption(d.data, '.ac-hemicycle .c-leyenda__list');
      const name = d.data.partyCode ? d.data.partyCode : 'desconocido';
      return `c-hemicycle__path c-hemicycle__path--current ${name} party-${name}`;
    })
    .style('fill', (d) => d.data.sFill)
    .on('mouseover', (d) => {
      svg.selectAll(`.hoverArc.party-${d.data.partyCode}`).transition().attr('opacity', 0.5);
      if (d.data.prev) {
        svg.selectAll(`.hoverArc.prev-${d.data.prev.partyCode}`).transition().attr('opacity', 0.5);
      }
      tooltip.show(d.data, elem.node(), d3.event.layerX, d3.event.layerY);
    })
    .on('mousemove', (d) => {
      tooltip.show(d.data, elem.node(), d3.event.layerX, d3.event.layerY);
    })
    .on('mouseout', (d) => {
      svg.selectAll(`.hoverArc.party-${d.data.partyCode}`).transition().attr('opacity', 0);
      if (d.data.prev) {
        svg.selectAll(`.hoverArc.prev-${d.data.prev.partyCode}`).transition().attr('opacity', 0);
      }
      tooltip.hide(elem.node());
    });

  pathCurrent.transition()
    .delay(options.animationDelay)
    .duration(options.animationDuration)
    .attrTween('d', (d) => {
      const is = d3.interpolate(-Math.PI / 2, d.startAngle);
      const ie = d3.interpolate(0, d.endAngle - d.startAngle);
      return (t) => {
        const newD = d;
        newD.startAngle = is(t);
        newD.endAngle = newD.startAngle + ie(t);
        return arcCurrent(newD);
      };
    });

  pathCurrent.exit().remove();

  const pathPrevious = svg.select('.c-hemicycle__hemi').datum(previousParties).selectAll('path.previous').data(pie);

  pathPrevious.enter().append('path')
    .attr('class', (d) => `c-hemicycle__path c-hemicycle__path--previous ${d.data.partyCode}`)
    .each((d) => {
      const newD = d;
      newD.previousParties = newD.data;
    })
    .on('mouseover', (d) => {
      tooltip.show(d.data, elem.node(), d3.event.layerX, d3.event.layerY);
    })
    .on('mousemove', (d) => {
      tooltip.show(d.data, elem.node(), d3.event.layerX, d3.event.layerY);
    })
    .on('mouseout', () => {
      tooltip.hide(elem.node());
    });

  pathPrevious.transition()
    .delay(options.animationDelay / 2)
    .duration(options.animationDuration)
    .attrTween('d', (d) => {
      const is = d3.interpolate(-Math.PI / 2, d.startAngle);
      const ie = d3.interpolate(0, d.endAngle - d.startAngle);
      return (t) => {
        const newD = d;
        newD.startAngle = is(t);
        newD.endAngle = newD.startAngle + ie(t);
        return arcPrevious(newD);
      };
    });

  pathPrevious.exit()
    .remove();

  const arcDelay = (delay) => (d) => {
    const angle = (d.startAngle + d.endAngle) / 2;
    return ((angle + Math.PI / 2) / Math.PI) * options.animationDuration + options.animationDelay + delay;
  };

  const labels = svg.select('.c-hemicycle__labels-years');
  if (previousParties) {
    if (window.innerWidth < 800) {
      labels.append('text')
        .attr('x', -radius / 2)
        .attr('y', '15px')
        .text(window.ac_labels.hemicycle.previous);
      labels.append('text')
        .attr('x', -radius)
        .attr('y', '15px')
        .text(window.ac_labels.hemicycle.current);
    } else {
      labels.append('text')
        .attr('x', '-110px')
        .attr('y', '15px')
        .text(window.ac_labels.hemicycle.previous);
      labels.append('text')
        .attr('x', '-230px')
        .attr('y', '15px')
        .text(window.ac_labels.hemicycle.current);
    }
  }


  const labelsCurrent = svg.select('g.c-hemicycle__labels--current').selectAll('text').data(pie(currentParties));

  labelsCurrent.enter()
    .append('text')
    .attr('dy', '.35em')
    .attr('class', 'c-hemicycle__escanos')
    .text((d) => (maxAngleToShowLabelCurrent < Math.abs(((d.startAngle - d.endAngle) * 100)) ? d.data.seats : null))
    .attr('transform', (d) => `translate(${arcCurrent.centroid(d)})`)
    .attr('text-anchor', () => 'middle')
    .style('opacity', 0);

  labelsCurrent.transition()
    .duration(100)
    .delay(arcDelay(options.animationDelay / 2))
    .style('opacity', 1);

  const labelsPrevious = svg.select('g.c-hemicycle__labels--previous').selectAll('text').data(pie(previousParties));

  labelsPrevious.enter()
    .append('text')
    .attr('dy', '.35em')
    .attr('class', 'c-hemicycle__escanos')
    .text((d) => (maxAngleToShowLabelPrev < Math.abs(((d.startAngle - d.endAngle) * 100)) ? d.data.seats : null))
    .attr('transform', (d) => `translate(${arcPrevious.centroid(d)})`)
    .attr('text-anchor', () => 'middle')
    .style('opacity', 0);

  labelsPrevious.transition()
    .duration(100)
    .delay(arcDelay(options.animationDelay / 2))
    .style('opacity', 1);

  if (options.showMayoria) {
    svg.append('g').attr('class', 'c-hemicycle__majority');
    svg.select('.c-hemicycle__majority').append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 10)
      .attr('y2', -radius)
      .attr('stroke-dasharray', '2,2');
  }
}

function init(selector, currentParties, previousParties, seats) {
  if (document.getElementById(selector)) {
    drawChart(selector, currentParties, previousParties, seats);
  }
}

module.exports = {
  init,
};
